// The `notification` wire shape — one inbox row, emitted by `GET
// /v1/notifications`, by the `notifications` topic of `/v1/streams`, and by the
// MCP `notification inbox` action.
//
// THREE surfaces, and they were already held together — by a hand-written
// TypeScript type (`NotificationView`) that lived in `packages/mcp` and was
// imported by `apps/api`. That kept the three honest, and it is why this
// migration finds no drift to report: the type WAS the seam.
//
// What it could not do is be published. A `type` has no runtime value, so
// `GET /v1/notifications` answered an untyped `Page` in the OpenAPI document
// and no generated client knew a single field of the row. A resource is the
// same seam plus a schema, so the shape reaches the document as well as the
// compiler — and `NotificationView` is now an alias of the wire type rather
// than a second declaration of it.
//
// One row per EVENT: the fanout writes a ledger row per (kind x channel)
// delivery and the read collapses them by `dedupe_key`, so an escalation fanned
// to email + push appears once.

import { z } from 'zod';
import {
  EmailCadence,
  EmailCadenceSchema,
  KIND_TIER,
  NotificationChannelSchema,
  NotificationKindSchema,
  NotificationStatus,
  NotificationStatusSchema,
  NotificationTier,
  NotificationTierSchema,
} from '../entities/notification';
import { defineResource } from './resource';

/**
 * The camelCase row the projection reads — structural, so the resource states
 * its own minimum rather than importing `@developerz/db`'s row type, which
 * `domain` may not depend on.
 */
export type NotificationEntity = {
  id: string;
  kind: z.infer<typeof NotificationKindSchema>;
  channel: z.infer<typeof NotificationChannelSchema>;
  status: z.infer<typeof NotificationStatusSchema>;
  title: string;
  body: string;
  deepLink: string;
  dedupeKey: string | null;
  payload: Record<string, unknown> | null;
  createdAt: Date;
  updatedAt: Date;
  sentAt: Date | null;
  readAt: Date | null;
};

/** The published body. Field names and nullability are the existing contract. */
export const NotificationWireSchema = z.object({
  id: z.string(),
  kind: NotificationKindSchema,
  channel: NotificationChannelSchema,
  /**
   * `NotificationStatusSchema`, not the bare `string` the hand-written
   * `NotificationView` widened it to. The store row has always been typed
   * `NotificationStatus`; the mirror lost that on the way to the client, so a
   * generated client learned nothing about the five values it can see.
   */
  status: NotificationStatusSchema,
  title: z.string(),
  body: z.string(),
  /** App-relative path the row opens — the deep link the bell navigates to. */
  deep_link: z.string(),
  /** The fanout key rows collapse on; null on a row the fanout did not dedupe. */
  dedupe_key: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  /** When the channel actually delivered; null while pending or on a failure. */
  sent_at: z.string().nullable(),
  /** When this EVENT was marked read; null while unread. */
  read_at: z.string().nullable(),
});

/** What a client should type against for an inbox row. */
export type NotificationWire = z.infer<typeof NotificationWireSchema>;

export const NotificationResource = defineResource<
  NotificationEntity,
  typeof NotificationWireSchema
>({
  schema: NotificationWireSchema,
  serialize: (n) => ({
    id: n.id,
    kind: n.kind,
    channel: n.channel,
    status: n.status,
    title: n.title,
    body: n.body,
    deep_link: n.deepLink,
    dedupe_key: n.dedupeKey,
    payload: n.payload,
    created_at: n.createdAt.toISOString(),
    updated_at: n.updatedAt.toISOString(),
    sent_at: n.sentAt ? n.sentAt.toISOString() : null,
    read_at: n.readAt ? n.readAt.toISOString() : null,
  }),
});

// ─── one EMAIL row of the caller's last 7 days, and why it was or was not sent ──

/** How many days of the ledger the estimate and the deliveries list read. */
export const NOTIFICATION_HISTORY_WINDOW_DAYS = 7;

/**
 * The statuses the deliveries list serves. `pending` is left out on purpose: a
 * pending row is an attempt still in flight, so it has no outcome to explain.
 */
export const NotificationDeliveryStatusSchema = z.enum([
  NotificationStatus.Sent,
  NotificationStatus.Failed,
  NotificationStatus.Suppressed,
  NotificationStatus.NoTarget,
  NotificationStatus.Deferred,
]);
export type NotificationDeliveryStatus = z.infer<typeof NotificationDeliveryStatusSchema>;

/** The closed reason code. The dashboard maps each value to one plain sentence. */
export const NotificationDeliveryWhy = {
  YourSetting: 'your_setting',
  AlwaysOn: 'always_on',
  /**
   * Every `suppressed` row that was not told earlier, whatever suppressed it —
   * a setting, the account's unsubscribe, or a rule that held the email back
   * although the setting was ON. The ledger keeps no reason that tells them
   * apart; see `deliveryWhy`.
   */
  TurnedOff: 'turned_off',
  NowhereToSend: 'nowhere_to_send',
  Batched: 'batched',
  AlreadyTold: 'already_told',
  Failed: 'failed',
} as const;
export const NotificationDeliveryWhySchema = z.enum(NotificationDeliveryWhy);
export type NotificationDeliveryWhy = z.infer<typeof NotificationDeliveryWhySchema>;

/** The stored facts one row is explained from. No preference is read. */
export type NotificationDeliveryEntity = {
  id: string;
  kind: z.infer<typeof NotificationKindSchema>;
  status: NotificationDeliveryStatus;
  title: string;
  sentAt: Date | null;
  /** The bundle that carried the row, with the cadence stored on that bundle. */
  bundle: { sentAt: Date | null; cadence: z.infer<typeof EmailCadenceSchema> } | null;
  /** An email for the same item reached this person in the 7 days before this row. */
  toldEarlier: boolean;
};

/**
 * Whether a stored bundle is a batch. A bundle with cadence `instant` is a
 * released single (#5696) — one email sent on its own the moment it landed —
 * so it is not a batch, and a sent row it carried is explained exactly like a
 * row with no bundle. On the wire that means `timing: 'instant'` and
 * `bundle: null`: a released single was never part of a batch, so the row must
 * not be shown one or called "batched".
 */
function isBatch(
  bundle: NotificationDeliveryEntity['bundle'],
): bundle is Exclude<NotificationDeliveryEntity['bundle'], null> {
  return bundle !== null && bundle.cadence !== EmailCadence.Instant;
}

/**
 * Why a row ended the way it did, from stored facts only (kind, status, the
 * bundle, the earlier telling), so the answer stays true after a setting changes.
 *
 * `turned_off` is every `suppressed` row that was not told earlier, whatever
 * suppressed it — it does not say the person's setting is off. Among the
 * writers (examples, not an exhaustive list; see
 * `apps/worker/src/notifications/` and `apps/worker/src/cron/email-bundle.ts`):
 * - email off for the kind (a preference or the default);
 * - the account's unsubscribe;
 * - a bundle item refused when its bundle is flushed;
 * - a rule that holds an email back although the setting is ON: a follow-up
 *   that is not owed, an escalation collapsed into the hour's summary, a copy
 *   the account already got by a routed address.
 * A webhook-channel cell can also be marked `suppressed` (one webhook carries an
 * event for the whole account), but this list serves email rows only, so such a
 * row never reaches this code.
 * The ledger persists no typed reason that tells them apart, so a consumer must
 * word `turned_off` as "not sent because of a setting or a rule" and must never
 * claim the person turned it off. Persisting a typed suppression reason on the
 * row is the follow-up that would make this code exact — it must cover every
 * writer, not only some.
 */
export function deliveryWhy(
  row: Pick<NotificationDeliveryEntity, 'kind' | 'status' | 'bundle' | 'toldEarlier'>,
): NotificationDeliveryWhy {
  switch (row.status) {
    case NotificationStatus.Failed:
      return NotificationDeliveryWhy.Failed;
    case NotificationStatus.NoTarget:
      return NotificationDeliveryWhy.NowhereToSend;
    case NotificationStatus.Suppressed:
      return row.toldEarlier
        ? NotificationDeliveryWhy.AlreadyTold
        : NotificationDeliveryWhy.TurnedOff;
    case NotificationStatus.Deferred:
      return NotificationDeliveryWhy.Batched;
    case NotificationStatus.Sent:
      if (isBatch(row.bundle)) return NotificationDeliveryWhy.Batched;
      return KIND_TIER[row.kind] === NotificationTier.Urgent
        ? NotificationDeliveryWhy.AlwaysOn
        : NotificationDeliveryWhy.YourSetting;
  }
}

/** The published row. No address: the ledger stores none, so none is invented. */
export const NotificationDeliveryWireSchema = z.object({
  id: z.string(),
  kind: NotificationKindSchema,
  tier: NotificationTierSchema,
  title: z.string(),
  status: NotificationDeliveryStatusSchema,
  /** When the email went out; null for a row that was not sent or is still waiting. */
  sent_at: z.string().nullable(),
  timing: z.enum(['instant', 'bundle']),
  bundle: z.object({ sent_at: z.string().nullable(), cadence: EmailCadenceSchema }).nullable(),
  why: NotificationDeliveryWhySchema.describe(
    'Every suppressed row not told earlier, for any reason (a setting, an unsubscribe, or a rule such as a collapse or a routed duplicate). Not proof the person turned email off.',
  ),
});
export type NotificationDeliveryWire = z.infer<typeof NotificationDeliveryWireSchema>;

export const NotificationDeliveryResource = defineResource<
  NotificationDeliveryEntity,
  typeof NotificationDeliveryWireSchema
>({
  schema: NotificationDeliveryWireSchema,
  serialize: (d) => ({
    id: d.id,
    kind: d.kind,
    tier: KIND_TIER[d.kind],
    title: d.title,
    status: d.status,
    sent_at: d.sentAt ? d.sentAt.toISOString() : null,
    timing: d.status === NotificationStatus.Deferred || isBatch(d.bundle) ? 'bundle' : 'instant',
    bundle: isBatch(d.bundle)
      ? {
          sent_at: d.bundle.sentAt ? d.bundle.sentAt.toISOString() : null,
          cadence: d.bundle.cadence,
        }
      : null,
    why: deliveryWhy(d),
  }),
});

/**
 * `GET /v1/me/notification-estimate`, and the MCP `estimate` action: about how
 * many emails a week the caller's settings mean, replayed from their own last
 * 7 days, and what each preset would total over the same events.
 */
export const NotificationEstimateWireSchema = z.object({
  window_days: z.literal(NOTIFICATION_HISTORY_WINDOW_DAYS),
  /** How many of the caller's own events the totals were replayed from. */
  events: z.number().int(),
  current: z.object({
    emails: z.number().int(),
    instant: z.number().int(),
    bundles: z.number().int(),
  }),
  presets: z.object({
    only_urgent: z.number().int(),
    recommended: z.number().int(),
    everything: z.number().int(),
  }),
  /** The event read hit its row cap, so every total is a floor. */
  truncated: z.boolean(),
});
export type NotificationEstimateWire = z.infer<typeof NotificationEstimateWireSchema>;
